import React from 'react';
import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type { VenueTypeCode } from '../../../../../features/landing/constants/venueTypes';
import { VenueType } from '../../../../../features/landing/constants/venueTypes';

interface VenueTypePatternProps {
  venueType: VenueTypeCode;
  opacity?: number;
}

export const VenueTypePattern = React.memo(function VenueTypePattern({
  venueType,
  opacity = 0.03,
}: VenueTypePatternProps) {
  const theme = useTheme();

  // Different CSS patterns per venue type
  const getPattern = (): string => {
    const gaming = theme.palette.venue.gaming;
    const snooker = theme.palette.venue.snooker;
    const kids = theme.palette.venue.kids;

    switch (venueType) {
      case VenueType.GAMING:
        // Pixel grid pattern
        return `
          repeating-linear-gradient(0deg, ${gaming} 0px, ${gaming} 1px, transparent 1px, transparent 20px),
          repeating-linear-gradient(90deg, ${gaming} 0px, ${gaming} 1px, transparent 1px, transparent 20px)
        `;
      case VenueType.SNOOKER:
        // Diagonal line pattern (baize texture)
        return `
          repeating-linear-gradient(45deg, ${snooker} 0px, ${snooker} 1px, transparent 1px, transparent 12px)
        `;
      case VenueType.KIDS_PLAYAREA:
        // Dotted confetti pattern
        return `
          radial-gradient(circle, ${kids} 1px, transparent 1px)
        `;
      default:
        return 'none';
    }
  };

  const getBackgroundSize = (): string => {
    switch (venueType) {
      case VenueType.GAMING:
        return '20px 20px';
      case VenueType.SNOOKER:
        return '12px 12px';
      case VenueType.KIDS_PLAYAREA:
        return '24px 24px';
      default:
        return '20px 20px';
    }
  };

  return (
    <Box
      aria-hidden="true"
      role="presentation"
      sx={{
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
        opacity,
        backgroundImage: getPattern(),
        backgroundSize: getBackgroundSize(),
      }}
    />
  );
});
